import intl from 'react-intl-universal';
import { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Card from '@mui/material/Card';
import CloseIcon from '@mui/icons-material/Close';

import api from 'utils/api';
import { isCurrentUserTaskManager } from 'utils/privilege';
import { Actions, StyledButton } from 'shared/modals/Styles';
import localData from 'utils/localData';
import { USER_COMMENT } from 'shared/constants/comment';
import { DOLPHIN_API } from 'shared/constants/apis';
import { getSelectedIssuesListTitle, getSelectedIssuesList } from './utils';
import ToggleStatus from './Status';
import ToggleState from './State';
import AssigneesReporter from './AssigneesReporter';
import SelectDoctorPopUpWindow from './SelectDoctorPopUpWindow';
import { modalBorderRadius, modalWidths, zIndexValues } from 'utils/styles';
import { HorizontalDivider } from './styles';
import { Box, Typography } from '@mui/material';
import logger from 'utils/logger';

const propTypes = {
  isOpened: PropTypes.bool,
  closeModal: PropTypes.func,
  selectedIssueIndexes: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSelectedIssueIndexes: PropTypes.func,
  issues: PropTypes.array.isRequired,
  doctors: PropTypes.array.isRequired,
  updateLocalProjectIssues: PropTypes.func,
  handleOpenIssueDetails: PropTypes.func,
  isSender: PropTypes.bool,
};

const log = logger.createLogger('BatchEditModal');

const BatchEditModal = ({
  isOpened,
  closeModal,
  selectedIssueIndexes,
  setSelectedIssueIndexes,
  issues,
  doctors,
  updateLocalProjectIssues,
  handleOpenIssueDetails,
  isSender,
}) => {
  const selectedIssues = selectedIssueIndexes.map((index) =>
    issues.find((issue) => issue.id === index)
  );
  const firstIssue = selectedIssues[0]; // logically selectedIssues must contain at least one item;
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalUpdateData, setConfirmModalUpdateData] = useState();
  const [, setConfirmModalInfo] = useState({
    source: '',
    destination: '',
  });
  const [selectDoctorModalOpen, setSelectDoctorModalOpen] = useState(false);

  const batchUpdateIssues = (updatedFields, issues) => {
    api.optimisticBatchUpdateIssues(`${DOLPHIN_API.ISSUES_BATCH_UPDATE}`, {
      issues,
      updatedFields,
      setLocalData: (issueId, fields) => {
        // this fields is either `updatedFields` or `currentFields` above;
        // this is the local action that is being executed
        updateLocalProjectIssues(issueId, fields);
      },
    });
  };

  const currentOrganizationId = localData.get(USER_COMMENT.CURRENT_ORGID);
  const srcOrgId = selectedIssues[0].srcOrgId.toString();
  const isClient = parseInt(srcOrgId) === parseInt(currentOrganizationId);
  const isTaskManager = isCurrentUserTaskManager();

  const handleConfirmModalYesButtonClick = async () => {
    await batchUpdateIssues(confirmModalUpdateData, selectedIssues);
    setConfirmModalOpen(false);
    setSelectedIssueIndexes([]);
  };

  const handleConfirmModalNoButtonClick = () => {
    setConfirmModalOpen(false);
  };

  return (
    <Fragment>
      <Modal
        data-testid="batch-edit-modal"
        disableEscapeKeyDown
        open={isOpened}
        onClose={closeModal}
        sx={{ zIndex: zIndexValues.batchEditModal }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Card
          sx={{
            minHeight: '30%',
            maxHeight: '75%',
            minWidth: '1000px',
            margin: '10px',
            paddingBottom: '30px',
            borderRadius: modalBorderRadius.sm,
          }}>
          <Grid container justifyContent="flex-end">
            <Grid size={12} display="flex" justifyContent="flex-end">
              <IconButton
                aria-label="delete"
                size="small"
                variant="empty"
                onClick={closeModal}
                sx={{ paddingRight: 1, paddingTop: 1 }}>
                <CloseIcon fontSize="medium" />
              </IconButton>
            </Grid>
          </Grid>

          <Box mt={1} mb={2} ml={4}>
            <Typography variant="h5" component="h2">
              {intl.get('shared_batch_edit_modal_text_edit_all')}
            </Typography>
          </Box>

          <Grid container columnSpacing={2} justifyContent="center">
            {/* batch change status */}
            <Grid size={3}>
              <Box display="flex" justifyContent="flex-end">
                {isTaskManager === true && (
                  <ToggleStatus
                    isClient={isClient}
                    issues={selectedIssues}
                    setConfirmModalInfo={setConfirmModalInfo}
                    setConfirmModalUpdateData={setConfirmModalUpdateData}
                    setConfirmModalOpen={setConfirmModalOpen}
                    setSelectDoctorModalOpen={setSelectDoctorModalOpen}
                  />
                )}
              </Box>
            </Grid>
            {/* batch change state */}
            <Grid size={3}>
              <Box display="flex" justifyContent="center">
                {!isSender && (
                  <ToggleState
                    selectedIssues={selectedIssues}
                    setConfirmModalInfo={setConfirmModalInfo}
                    setConfirmModalUpdateData={setConfirmModalUpdateData}
                    setConfirmModalOpen={setConfirmModalOpen}
                  />
                )}
              </Box>
            </Grid>
            <Grid size={3}>
              {!isSender && (
                <AssigneesReporter
                  issue={firstIssue}
                  doctors={doctors}
                  setConfirmModalInfo={setConfirmModalInfo}
                  setConfirmModalUpdateData={setConfirmModalUpdateData}
                  setConfirmModalOpen={setConfirmModalOpen}
                />
              )}
            </Grid>
          </Grid>

          <Box my={1} ml={4}>
            <Typography variant="h5" component="h2">
              {intl
                .get('shared_batch_edit_modal_text_already_selected')
                .replace('{arg1}', `${selectedIssues.length}`)}
            </Typography>
          </Box>

          {getSelectedIssuesListTitle()}

          <HorizontalDivider />

          <Box sx={{ overflowY: 'auto', maxHeight: '500px' }}>
            {getSelectedIssuesList(
              doctors,
              selectedIssues,
              handleOpenIssueDetails
            )}
          </Box>
        </Card>
      </Modal>

      {confirmModalOpen && (
        <Modal
          disableEscapeKeyDown
          open={confirmModalOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '50vw',
          }}>
          <Card
            sx={{
              maxHeight: '50%',
              width: modalWidths.xs,
              borderRadius: modalBorderRadius.sm,
              padding: '30px',
            }}>
            <Box>
              <Grid container justifyContent="center" rowSpacing={1} mb={5}>
                <Grid size={12} display="flex" justifyContent="flex-start">
                  <Typography variant="h5">
                    {intl
                      .get('shared_batch_edit_modal_text_hint_next')
                      .replace('{arg1}', `${selectedIssues.length}`)}
                  </Typography>
                </Grid>
                <Grid size={12} display="flex" justifyContent="center">
                  <Typography variant="caption" color="error">
                    * {intl.get('shared_batch_edit_modal_text_status_warning')}
                  </Typography>
                </Grid>
              </Grid>
              <Actions>
                <StyledButton
                  variant="primary"
                  onClick={handleConfirmModalYesButtonClick}>
                  {intl.get('kanban_confirm_popup_window_button_yes')}
                </StyledButton>
                <StyledButton hollow onClick={handleConfirmModalNoButtonClick}>
                  {intl.get('kanban_confirm_popup_window_button_no')}
                </StyledButton>
              </Actions>
            </Box>
          </Card>
        </Modal>
      )}

      {selectDoctorModalOpen && (
        <SelectDoctorPopUpWindow
          doctors={doctors}
          selectedIssues={selectedIssues}
          batchUpdateIssues={batchUpdateIssues}
          confirmModalUpdateData={confirmModalUpdateData}
          setConfirmModalUpdateData={setConfirmModalUpdateData}
          selectDoctorModalOpen={selectDoctorModalOpen}
          setSelectDoctorModalOpen={setSelectDoctorModalOpen}
        />
      )}
    </Fragment>
  );
};

BatchEditModal.propTypes = propTypes;

export default BatchEditModal;
